import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ArrowContainer, Popover } from 'react-tiny-popover'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Box, BoxProps, Button, Flex, Radio, Stack, Text, BaseButton } from '../../vanilla'
import { Link } from '../link'
import {
  useBasketStore,
  useOrderStore,
  useProductStore,
} from '../../store/hooks/useStore'
import { CloseIcon, InfoIcon } from '../icons'
import { baseThemeContract } from '../../vanilla/vars.css'
import { OrderSummaryProgressBars } from './progress-bars'
import { DeliveryDateTime } from '../book-delivery/delivery-date-time'
import { useExitEditModeModal } from '../../pages/cart/use-exit-order-edit-mode-context'
import { useBaglessStores } from '../../hooks/use-bagless-stores'
import { useCarrierBag } from './use-carrier-bag'
import { RefundDetails } from './refund-details'
import { useBeginCheckoutTracking } from '../../analytics/click-event-tracking'
import { useBonusCard } from '../../store/hooks/useBonusCard'
import { handleClosePopoverEvents, isMobile } from '../../utils/utils'
import { BasketToCheckoutButton } from '../../pages/cart/partials/basket-to-checkout-button'
interface OrderSummaryProps extends BoxProps<'div'> {
  tabledMode?: boolean
  showItemsCount?: boolean
  showOrderTotal?: boolean
  title?: string
  minWidthItemsAmount?: number
  rowsContainerProps?: BoxProps<'div'>
  checkoutButtonText?: string
  handleCheckoutButtonClick?: () => void
  isCheckout?: boolean
  includeBag?: boolean
  setIncludeBag?: (value: boolean) => void
}

export const OrderSummary = observer((props: OrderSummaryProps) => {
  const {
    title,
    tabledMode,
    rowsContainerProps,
    minWidthItemsAmount,
    showItemsCount,
    showOrderTotal = true,
    checkoutButtonText,
    handleCheckoutButtonClick,
    isCheckout = false,
    includeBag,
    setIncludeBag,
    ...rest
  } = props

  const { editMode, isPaymentRefunding, orderTotal, orderEditOrderTotal, orderTotalWithoutBC } = useOrderStore()
  const { currentStoreMessage } = useBaglessStores()
  const { productsById } = useProductStore()
  const { lastFour } = useBonusCard(true)
  const { isCarrierBagEditable } = useCarrierBag()
  const exitEditModeModal = useExitEditModeModal()
  const total = editMode ? orderEditOrderTotal : orderTotal

  const basketStore = useBasketStore()
  const { basket: order, calculatedDeliveryCost, orderDiscount } = basketStore

  const [smallBasketPopover, smallBasketPopoverSetIsOpen] = useState(false)
  const sendBeginCheckout = useBeginCheckoutTracking(productsById)

  const handleClick = () => {
    handleCheckoutButtonClick?.()
    sendBeginCheckout(order, order?.productItems)
  }

  useEffect(() => {
    handleClosePopoverEvents(smallBasketPopover, smallBasketPopoverSetIsOpen, 'small-basket-popover')
  }, [smallBasketPopover])

  return (
    <Stack
      bg="white"
      spacing="0px"
      align="flex-start"
      width="full"
      maxWidth={isCheckout ? '380px' : 'auto'}
      {...(rest || {})}
    >
      <Stack spacing="16px" width="full" align="flex-start" paddingTop="16px">
        {isCheckout && <DeliveryDateTime isCheckout={isCheckout} popover={false} />}
        <Stack width="full">
          {isCheckout && (
            <Box
              borderTop="1px"
              borderBottom="1px"
              borderColor="border1"
              padding={['16px', '16px', '16px']}
            >
              <Flex justifyContent="space-between">
                <Text variant="unstyled">Number of items</Text>
                <Text variant="unstyled" data-test-selector="items-count">{basketStore.count}</Text>
              </Flex>
            </Box>
          )}
          {/* Basket Subtotal Row */}
          <Flex justify="space-between" paddingX="16px" {...(rowsContainerProps || {})}>
            <Text variant="text3">
              <FormattedMessage defaultMessage="Subtotal" />
            </Text>
            <Flex align="center">
              {showItemsCount && (
                <Text
                  variant="text3"
                  textAlign="center"
                  data-test-selector="items-count"
                  style={{ minWidth: minWidthItemsAmount }}
                >{`${basketStore.count} Item(s)`}</Text>
              )}
              <Text variant="text3" data-test-selector="subtotal-value">
                {order?.currency && (
                  <FormattedNumber
                    style="currency"
                    currency={order?.currency}
                    value={basketStore?.subtotal}
                  />
                )}
              </Text>
            </Flex>
          </Flex>
          {/* Promotions Discount  Row */}
          {orderDiscount > 0 && (
            <Flex justify="space-between" paddingX="16px" {...(rowsContainerProps || {})}>
              <Text variant="text3" color="accent1" data-test-selector="promotional-discount-label">
                <FormattedMessage defaultMessage="Promotional Discount" />
              </Text>
              <Text variant="text3" color="accent1" data-test-selector="promotional-discount-value">
                {order?.currency && (
                  <FormattedNumber
                    style="currency"
                    currency={order?.currency}
                    value={-orderDiscount}
                  />
                )}
              </Text>
            </Flex>
          )}
          {/* Basket total Row */}
          <Flex justify="space-between" paddingX="16px" {...(rowsContainerProps || {})}>
            <Text variant="text3">
              <FormattedMessage defaultMessage="Basket total" />
            </Text>
            <Text variant="text3" data-test-selector="basket-total-value">
              {order?.currency && (
                <FormattedNumber
                  style="currency"
                  currency={order?.currency}
                  value={order?.productTotal || 0}
                />
              )}
            </Text>
          </Flex>

          {/* Basket Delivery Row */}
          <Flex justify="space-between" paddingX="16px" {...(rowsContainerProps || {})}>
            <Text variant="text3" data-test-selector="delivery-label">
              <FormattedMessage defaultMessage="Delivery" />
            </Text>

            <Text variant="text3" data-test-selector="delivery-value">
              {isNaN(calculatedDeliveryCost) ? (
                '-'
              ) : calculatedDeliveryCost === 0 ? (
                'FREE'
              ) : (
                <FormattedNumber
                  style="currency"
                  currency={order?.currency}
                  value={calculatedDeliveryCost}
                />
              )}
            </Text>
          </Flex>

          {/* - Small Basket Charge */}
          {basketStore.basketSmallBasketCharge != null && basketStore.basketSmallBasketCharge != 0 && (
            <Flex
              justify="space-between"
              paddingX="12px"
              bg="gray50"
              marginX="4px"
              {...(rowsContainerProps || {})}
            >
              <Flex align="center" gap="8px">
                <Text variant="text4">Small Basket Fee</Text>
                <Popover
                  positions={['top']}
                  align="center"
                  reposition={true}
                  containerStyle={{ zIndex: baseThemeContract.zIndex.popover }}
                  onClickOutside={() => smallBasketPopoverSetIsOpen(false)}
                  isOpen={smallBasketPopover}
                  content={({ position, childRect, popoverRect }) => (
                    <ArrowContainer
                      position={position}
                      childRect={childRect}
                      popoverRect={popoverRect}
                      arrowColor="white"
                      arrowSize={10}
                      arrowStyle={{ zIndex: baseThemeContract.zIndex.popover }}
                    >
                      <Stack
                        spacing="16px"
                        paddingX="20px"
                        paddingTop="20px"
                        paddingBottom="32px"
                        bg="white"
                        style={{
                          maxWidth: isMobile() ? "100vw" : 420,
                          width: isMobile() ? "calc(100vw - 20px)" : "auto",
                          boxShadow: "0 10px 25px 0 rgb(0 0 0 / 40%)",
                        }}
                        className="small-basket-popover"
                      >
                        <Flex align="center" justify="space-between" gap="4px">
                          <Text variant="heading0">Small Basket Fee</Text>
                          <CloseIcon marginLeft="8px" boxSize="30px" data-test-selector="small-basket-popover-close-icon" onClick={() => smallBasketPopoverSetIsOpen(false)} />
                        </Flex>
                        <Text variant="text3" lineHeight="short">
                          Orders below £{basketStore.basketSmallBasketChargeThreshold} will be charged a small basket fee of £{basketStore.basketSmallBasketCharge}
                        </Text>
                      </Stack>
                    </ArrowContainer>
                  )}
                >
                  <InfoIcon
                    color="gray800"
                    boxSize="16px"
                    className="small-basket-popover"
                    data-test-selector="small-basket-info-icon"
                    onMouseLeave={() => smallBasketPopoverSetIsOpen(true)}
                  />
                </Popover>
              </Flex>

              <Text
                variant="text3"
                position="relative"
                data-test-selector="small-basket-charge-value"
                style={{ right: showItemsCount ? -4 : 'initial' }}
              >
                {isNaN(basketStore.basketSmallBasketCharge) ? (
                  '-'
                ) : (
                  <FormattedNumber
                    style="currency"
                    currency={order?.currency}
                    value={basketStore.basketSmallBasketCharge}
                  />
                )}
              </Text>
            </Flex>
          )}

          {/* - End of SBC */}

          {/* Basket Bags and Picking Price Row */}
          {isCarrierBagEditable ? (
            <Box paddingX="8px" paddingY="4px" marginX="8px">
              <Flex gap="16px">
                <Radio
                  label={
                    <>
                      Use carrier bags (
                      <FormattedNumber
                        style="currency"
                        currency={order?.currency}
                        value={basketStore.carrierBagCharge}
                      />
                      )
                    </>
                  }
                  value="true"
                  checked={includeBag === true}
                  name="carrierBags"
                  onChange={() => {
                    setIncludeBag && setIncludeBag(true)
                  }}
                />
                <Radio
                  value="false"
                  label="No carrier bags"
                  checked={includeBag === false}
                  name="carrierBags"
                  onChange={() => {
                    setIncludeBag && setIncludeBag(false)
                  }}
                />
              </Flex>
            </Box>
          ) : (
            <Flex
              justify="space-between"
              paddingX="12px"
              marginX="4px"
              {...(rowsContainerProps || {})}
            >
              <Flex align="center" gap="8px">
                <Text variant="text4">{currentStoreMessage}</Text>
              </Flex>

              <Text
                variant="text3"
                position="relative"
                style={{ right: showItemsCount ? -4 : 'initial' }}
                data-test-selector="bags-packing-value"
              >
                {isNaN(basketStore.basketCarrierBagCharge) ? (
                  '-'
                ) : (
                  <FormattedNumber
                    style="currency"
                    currency={order?.currency}
                    value={basketStore.basketCarrierBagCharge}
                  />
                )}
              </Text>
            </Flex>
          )}

          {/* Basket Bonus Card Row */}
          {basketStore?.bonusCardStatus?.bonusCardUsed && isCheckout && (
            <Flex justify="space-between" paddingX="16px" {...(rowsContainerProps || {})}>
              <Text variant="text3" data-test-selector="bonus-card-label">{`Bonus Card ****${lastFour}`}</Text>
              <Text variant="text3" data-test-selector="bonus-card-value">
                <FormattedNumber
                  currency={order?.currency}
                  style="currency"
                  value={
                    basketStore?.bonusCardStatus?.leftToPay 
                    ? total - basketStore?.bonusCardStatus?.leftToPay
                    : total
                  }
                />
              </Text>
            </Flex>
          )}
        </Stack>
        {/* Progress bars */}
        {!tabledMode && !editMode && (
          <Box paddingX="16px" width="full">
            <OrderSummaryProgressBars border={true}/>
          </Box>
        )}

        {/* Order Total */}
        {showOrderTotal && (
          <Stack
            spacing="16px"
            width="full"
            borderTop="1px"
            borderBottom="1px"
            borderColor="gray200"
            paddingX="16px"
            paddingY="16px"
            {...(rowsContainerProps || {})}
          >
            <Flex width="full" justify="space-between">
              {!editMode ? (
                <Text variant="text6">{isCheckout ? 'Total' : 'Order Total'}</Text>
              ) : (
                <Text variant="text6" data-test-selector="order-total-label">Amount to pay / refund </Text>
              )}
              <Flex align="center">
                {showItemsCount && (
                  <Text
                    variant="text6"
                    textAlign="center"
                    data-test-selector="items-count"
                    style={{ minWidth: minWidthItemsAmount }}
                  >{`${basketStore.count} Item(s)`}</Text>
                )}
                {!editMode ? (
                  <Text variant="text6" data-test-selector="order-total-value">
                    {order?.currency && (
                      <FormattedNumber
                        style="currency"
                        currency={order?.currency}
                        value={(orderTotal || order?.productTotal) ?? 0}
                      />
                    )}
                  </Text>
                ) : (
                  <Text variant="text6" data-test-selector="order-edit-total-value">
                    {order?.currency && (
                      <FormattedNumber
                        style="currency"
                        currency={order?.currency}
                        value={orderEditOrderTotal}
                      />

                    )}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Stack>
        )}
        {isCheckout && editMode && (
          <>
            {isPaymentRefunding ? (
              <RefundDetails />
            ) : (
              <Flex
                paddingX="16px"
                justify="space-between"
                width="full"
                borderBottom="1px"
                paddingBottom="20px"
                borderColor="gray200"
              >
                <Text variant="heading1">Amount left to pay</Text>
                <Text variant="heading1" data-test-selector="amount-left-to-pay-value">
                  {order?.currency && (
                    <FormattedNumber
                      style="currency"
                      currency={order?.currency}
                      value={orderTotalWithoutBC}
                    />
                  )}
                </Text>
              </Flex>
            )}
          </>
        )}
        {!tabledMode && (
          <Flex
            gap="16px"
            direction={{ desktop: 'row', mobile: 'column' }}
            paddingX="16px"
            width="full"
            paddingBottom="16px"
          >
            {editMode ? (
              <Button
                variant="outlineDark"
                data-test-selector="discard-changes-button"
                size="sm"
                width={['full', 'full', 'half']}
                onClick={() => {
                  exitEditModeModal?.onOpen()
                }}
              >
                Discard Changes and Exit
              </Button>
            ) : (
              <BaseButton
                as={Link}
                variant="outlineDark"
                to="/"
                width={['full', 'full', 'half']}
                size="sm"
                data-test-selector="continue-shopping-button"
              >
                <Text
                  variant="unstyled"
                  data-test-selector="continue-shopping"
                  fontSize="inherit"
                  fontWeight="inherit"
                  color={{ hover: 'gray700', default: 'gray800' }}
                >
                  Continue Shopping
                </Text>
              </BaseButton>
            )}
            <BasketToCheckoutButton
              width={['full', 'full', 'half']}
              data-test-selector="checkout-securely-button"
              checkoutButtonText={checkoutButtonText ?? ''}
              onClick={() => handleClick()}
            />
          </Flex>
        )}
      </Stack>
    </Stack>
  )
})

